import { z } from 'zod';

/**
 * AI Tutor Policy v0 contract (PRE-20). System Design §10.2.
 *
 * The tutor never hands the learner a final answer: it produces a structured,
 * Socratic reply that escalates by hint level. The LLM is asked for JSON matching
 * `tutorReplySchema`; the server parses it, and any schema/policy violation is
 * retried or replaced by a deterministic fallback hint (see `lib/llm/tutor.ts`).
 */

/** Kind of turn the tutor produced. `final_explanation` is only legal post-complete. */
export const TUTOR_REPLY_TYPES = ['hint', 'question', 'feedback', 'final_explanation'] as const;
export type TutorReplyType = (typeof TUTOR_REPLY_TYPES)[number];

/** What the learner should do next. */
export const TUTOR_NEXT_ACTIONS = ['answer', 'reflect', 'continue', 'complete'] as const;
export type TutorNextAction = (typeof TUTOR_NEXT_ACTIONS)[number];

export const tutorPolicyFlagsSchema = z.object({
  /** Model believes it revealed the full solution (guard also checks heuristically). */
  gave_direct_answer: z.boolean(),
  /** Learner explicitly asked for the solution; tutor stays Socratic (BR-35). */
  direct_answer_requested: z.boolean(),
  /** Model needs more of the learner's attempt before it can help. */
  needs_more_context: z.boolean(),
});
export type TutorPolicyFlags = z.infer<typeof tutorPolicyFlagsSchema>;

/** The structured reply the LLM must emit — parsed server-side. */
export const tutorReplySchema = z.object({
  reply_type: z.enum(TUTOR_REPLY_TYPES),
  message: z.string().min(1).max(2000),
  hint_level_used: z.number().int().min(0).max(4),
  next_action: z.enum(TUTOR_NEXT_ACTIONS),
  policy_flags: tutorPolicyFlagsSchema,
});
export type TutorReply = z.infer<typeof tutorReplySchema>;

/** Request body for `POST /v1/sessions/:id/tutor`. */
export const tutorTurnInputSchema = z.object({
  sessionItemId: z.string().min(1),
  /** Free-text learner message / question / partial attempt. Treated as DATA. */
  message: z.string().min(1).max(4000),
});
export type TutorTurnInput = z.infer<typeof tutorTurnInputSchema>;

export interface TutorTurnResponse {
  session_item_id: string;
  /** True when the deterministic fallback answered (LLM never produced a valid reply). */
  fallback: boolean;
  reply: TutorReply;
}
